/**
 * Per-field DISPLAY normalizers applied inside `createFieldHook`'s `select`
 * (MOR-887).
 *
 * A field hook's `select` normally returns the raw field value. For fields
 * whose cache value churns below display resolution — the projector-driven
 * `outcomes[].conviction` being the motivating case — the leaf re-renders every
 * tick even though nothing visible moved. A normalizer registered here rounds
 * the selected value to display resolution so TanStack's `replaceEqualDeep`
 * collapses an unchanged-display result to the previous reference and the leaf
 * bails.
 *
 * Keyed by `${specName}:${field}` (the field hook's factory name + the field
 * arg). The cache stays full-precision — only the per-consumer selected value
 * is rounded.
 */

import { roundOutcomeConvictionForDisplay } from '../../helpers/conviction-display.js';

/**
 * A normalizer maps a field value to a display-rounded value of the SAME type
 * (`T` in, `T` out — never a shape change). The registry is heterogeneous across
 * fields, so it stores the widened `<unknown>` default; each normalizer is
 * authored against its own concrete field type at its definition site.
 */
export type FieldDisplayNormalizer<T = unknown> = (value: T) => T;

const NORMALIZERS: ReadonlyMap<string, FieldDisplayNormalizer> = new Map([
  ['useGetMarketsField:outcomes', roundOutcomeConvictionForDisplay],
]);

export function getFieldDisplayNormalizer(
  specName: string,
  field: string,
): FieldDisplayNormalizer | undefined {
  return NORMALIZERS.get(`${specName}:${field}`);
}
